import React, { useState, useEffect } from "react";
import styles from "../../assets/scss/Dashboard.module.scss";
import {
  FiBox,
  FiTruck,
  FiClock,
  FiUserPlus,
  FiDollarSign,
} from "react-icons/fi";
import { Doughnut } from "react-chartjs-2";
import GaugeChart from "react-gauge-chart";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import { useGetDashboardQuery, useGetUserByIdQuery } from "../../slices/admin/admin";
import { BaseURL } from "../../lib/api";
import dummyProfile from "../../assets/images/Profile.png";
import { getUserInfo } from '../../utils/user.util';
import RetailOwnerCustomer from "./RetailOwnerCustomer";
import FarmerDashboard from "./FarmerDashboard";
import EmployeeDashboard from "./EmployeeDashboard";
import LogisticDashboard from "./LogisticDashboard";
import PopupWrapper from '../../Components/Common/PopupWrapper';
import popupStyles from "../../assets/scss/Plans/Addplans.module.scss";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from 'react-toastify';
import DashboardNotification from "../../Components/Dashboard/DashboardNotification";


ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetDashboardQuery();
  const user = getUserInfo();

  const [showExpiryPopup, setShowExpiryPopup] = useState(false);
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [expiryDays, setExpiryDays] = useState(0);
  const [inviteCount, setInviteCount] = useState(0);

  const checkInvites = () => {
    const hasInviteShown = sessionStorage.getItem('invitePopupShown');
    const pendingInvites = user?.invites?.filter(invite => !invite.isAccepted) || [];
    if (pendingInvites.length > 0 && !hasInviteShown && !showExpiryPopup) {
      setInviteCount(pendingInvites.length);
      setShowInvitePopup(true);
    }
  };

  const handleInvitePopupClose = () => {
    setShowInvitePopup(false);
    sessionStorage.setItem('invitePopupShown', 'true');
  };

  useEffect(() => {
    if (user?.planExpiredAt) {
      const today = new Date();
      const expiry = new Date(user.planExpiredAt);

      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);

      const diffTime = expiry.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const hasShown = sessionStorage.getItem('planExpiryPopupShown');

      if (diffDays <= 7 && !hasShown) {
        setExpiryDays(diffDays);
        setShowExpiryPopup(true);
      } else if (!showExpiryPopup) {
        checkInvites();
      }
    } else {
      if (!showExpiryPopup) checkInvites();
    }
  }, [user, showExpiryPopup]);

  const handleExpiryPopupClose = () => {
    setShowExpiryPopup(false);
    sessionStorage.setItem('planExpiryPopupShown', 'true');
    checkInvites();
  };

  useEffect(() => {
    if (data?.data?.notifications) {
      const storedNotifiedIds = JSON.parse(sessionStorage.getItem('notifiedNotificationIds') || '[]');
      const notifiedSet = new Set(storedNotifiedIds);
      const unreadNotifications = data.data.notifications.filter(note => note.isRead === false);

      const newNotifiedIds = [...storedNotifiedIds];
      let updated = false;

      unreadNotifications.forEach(note => {
        if (!notifiedSet.has(note._id)) {
          toast(<DashboardNotification notification={note} />, {
            position: "top-right",
            autoClose: 10000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            icon: false,
            className: "dashboard-notification-toast",
            bodyClassName: "dashboard-notification-toast-body",
          });
          newNotifiedIds.push(note._id);
          notifiedSet.add(note._id);
          updated = true;
        }
      });

      if (updated) {
        sessionStorage.setItem('notifiedNotificationIds', JSON.stringify(newNotifiedIds));
      }
    }
  }, [data]);

  const [tooltip, setTooltip] = useState({
    show: false,
    text: "",
    x: 0,
    y: 0,
  });

  //  console.log("dashboard666", user);

  const localUser = getUserInfo();
  const userId = localUser?._id;

  const { data: userData } = useGetUserByIdQuery(userId, { skip: !userId });

  const bannerPath = userData?.result?.[0]?.bannerImage;

  const getFirstImage = (path) => {
    if (!path) return null;
    if (Array.isArray(path)) return path[0];
    if (typeof path === 'string' && path.startsWith('[')) {
      try {
        const parsed = JSON.parse(path);
        return Array.isArray(parsed) ? parsed[0] : path;
      } catch (e) {
        return path;
      }
    }
    return path;
  };

  const firstBanner = getFirstImage(bannerPath);
  const bannerImage = firstBanner
    ? firstBanner.startsWith("http")
      ? firstBanner
      : `${BaseURL}/${firstBanner}`
    : "https://via.placeholder.com/400x150?text=No+Banner+Image";

  if (isLoading) return <p>Loading...</p>;

  // Tooltip handlers
  const handleMouseEnter = (e, label, amount) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      show: true,
      text: `${label}: ₹${amount}`,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", x: 0, y: 0 });
  };

  const renderDashboardContent = () => {
    if (user?.type === "retailowner" || user?.type === "customer") {
      return <RetailOwnerCustomer />;
    }

    if (user?.type === "farmer") {
      return <FarmerDashboard />;
    }

    if (user?.type === "employee") {
      return <EmployeeDashboard />;
    }

    if (user?.type === "logistic") {
      return <LogisticDashboard />;
    }

    const dashboard = data?.data || {};

    const orderData = {
      labels: ["Total Orders", "Need Attention", "My Orders", "Pre Order Count"],
      datasets: [
        {
          data: [
            dashboard.totalOrders || 0,
            dashboard.orderNeedAttention || 0,
            dashboard.totalMyOrders || 0,
            dashboard.preOrderCount || 0,
          ],
          backgroundColor: ["#08a045", "#f8bb49", "#dbf3c9", "#c3f550"],
          borderWidth: 0,
          circumference: 360,
          rotation: -90,
          cutout: "60%",
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "left",
        },
      },
    };

    const todaySaleAmount = dashboard.todaySaleAmount || 0;
    const todayPendingSaleAmount = dashboard.todayPendingSaleAmount || 0;
    const totalSales = todaySaleAmount + todayPendingSaleAmount;

    const salePayableRatio = totalSales === 0 ? 0.5 : todaySaleAmount / totalSales;
    const saleReceivableRatio = totalSales === 0 ? 0.5 : todayPendingSaleAmount / totalSales;

    const todayPurchaseAmount = dashboard.todayPurchaseAmount || 0;
    const todayPendingPurchaseAmount = dashboard.todayPendingPurchaseAmount || 0;

    const totalPurchases = todayPurchaseAmount + todayPendingPurchaseAmount;

    const purchasePayableRatio = totalPurchases === 0 ? 0.5 : todayPurchaseAmount / totalPurchases;
    const purchaseReceivableRatio = totalPurchases === 0 ? 0.5 : todayPendingPurchaseAmount / totalPurchases;

    return (
      <div className={styles.dashboard}>
        <div className={styles.grid}>
          {/* Orders */}
          <div className={styles.card}>
            <div className={styles.header}>
              <h4 className={styles.heading}>Order Overview</h4>
              <span onClick={() => navigate("/dashboard/Ordertable")}>
                View all
              </span>
            </div>
            <div className={styles.orderChartWrapper}>
              <div className={styles.chartContainer}>
                {/* <Doughnut data={orderData} options={options} /> */}
                <Doughnut options={options} data={orderData} />
              </div>
            </div>
          </div>

          {/* Inventory */}
          <div className={styles.card}>
            <div className={styles.header}>
              <h4 className={styles.heading}>Inventory Overview</h4>
              <span onClick={() => navigate("/dashboard/InventoryTable")}>
                View all
              </span>
            </div>
            <div className={styles.overviewGrid}>
              <div className={`${styles.box} ${styles.orange}`}>
                <FiBox className={styles.icon} />
                <h3 className="notranslate">{dashboard.categoryCount || 0}</h3>
                <p>Item Category lists</p>
              </div>
              <div className={`${styles.box} ${styles.purple}`}>
                <FiTruck className={styles.icon} />
                <h3 className="notranslate">{dashboard.totalAvailableInventoryItems || 0}</h3>
                <p >Sum of the Item lists</p>
              </div>
            </div>

            <div className={styles.overviewGrid}>
              <div className={`${styles.box} ${styles.pink}`}>
                <span className={styles.icon}>₹</span>   <h3 className="notranslate">
                  {Number(dashboard.totalInventoryBuyingAmount || 0).toFixed(2)}
                </h3>
                <p>Total Inventory Value</p>
              </div>
            </div>
          </div>

          {/* Banner Slider */}
          {/* <div className={styles.bannerCard}>
            <div className={styles.header}>
              <h4 className={styles.heading}>Banner</h4>
              <span
                style={{ cursor: 'pointer', color: '#4CAF50', fontSize: '13px', fontWeight: 500 }}
                onClick={() => navigate('/dashboard/BannerSettings')}
              >
                View All
              </span>
            </div>
            <div className={styles.bannerSliderWrapper}>
              {data?.data?.bannerImages?.length > 0 ? (
                <Slider
                  dots={true}
                  infinite={true}
                  speed={800}
                  slidesToShow={1}
                  slidesToScroll={1}
                  autoplay={true}
                  autoplaySpeed={3500}
                  arrows={false}
                >
                  {data.data.bannerImages.map((img, idx) => (
                    <div key={idx} className={styles.bannerSlide}>
                      <img
                        src={typeof img === 'string' && img.startsWith('http') ? img : `${BaseURL}/${String(img).replace(/^\/+/, '')}`}
                        alt={`banner-${idx}`}
                        className={styles.bannerimg}
                        onError={(e) => (e.target.src = dummyProfile)}
                      />
                    </div>
                  ))}
                </Slider>
              ) : (
                <div className={styles.boxs}>
                  <img className={styles.bannerimg} src={bannerImage} alt="Store Banner" />
                </div>
              )}
            </div>
          </div> */}


           <div className={styles.card}>
            <div className={styles.overviewChartContainer}>

              <div className={styles.chartSection}>
                <div className={styles.header}>
                  <h4 className={styles.heading} onClick={() => navigate("/dashboard/billing", { state: { activeTab: "received" } })} style={{ cursor: "pointer" }}>Today’s Sales Overview</h4>
                  <span onClick={() => navigate("/dashboard/billing", { state: { activeTab: "received" } })}>
                    View all
                  </span>
                </div>
                <div className={styles.gaugeScaleWrapper}>
                  <GaugeChart
                    id="credit-balance-meter-sales"
                    nrOfLevels={20}
                    arcsLength={[salePayableRatio, saleReceivableRatio]}
                    colors={["#bfb8da", "#e8b7d4"]}
                    percent={saleReceivableRatio}
                    arcPadding={0.02}
                    needleColor="#222"
                    needleBaseColor="#888"
                    textColor="#000"
                    formatTextValue={() => ""}
                  />

                  <div
                    className={`${styles.gaugeHoverArea} ${styles.left}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Sales received amount", todaySaleAmount)}
                    onMouseLeave={handleMouseLeave}
                  />
                  <div
                    className={`${styles.gaugeHoverArea} ${styles.right}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Sales Pending amount", todayPendingSaleAmount)}
                    onMouseLeave={handleMouseLeave}
                  />

                  {tooltip.show && (
                    <div
                      style={{
                        position: "fixed",
                        top: tooltip.y,
                        left: tooltip.x,
                        transform: "translate(-50%, -100%)",
                        background: "#333",
                        color: "#fff",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        zIndex: 1000,
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        pointerEvents: "none"
                      }}
                    >
                      {tooltip.text}
                    </div>
                  )}

                  <div className={styles.gaugeLabelsContainer}>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Sales received amount</span>
                      <strong>₹ {todaySaleAmount}</strong>
                    </div>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Sales Pending amount</span>
                      <strong>₹ {todayPendingSaleAmount}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.chartSection}>
                <div className={styles.header}>
                  <h4 className={styles.heading} onClick={() => navigate("/dashboard/billing", { state: { activeTab: "payable" } })} style={{ cursor: "pointer" }}>Today’s Purchase Overview</h4>
                  <span onClick={() => navigate("/dashboard/billing", { state: { activeTab: "payable" } })}>
                    View all
                  </span>
                </div>
                <div className={styles.gaugeScaleWrapper}>
                  <GaugeChart
                    id="credit-balance-meter-purchase"
                    nrOfLevels={20}
                    arcsLength={[purchasePayableRatio, purchaseReceivableRatio]}
                    colors={["#bfb8da", "#e8b7d4"]}
                    percent={purchaseReceivableRatio}
                    arcPadding={0.02}
                    needleColor="#222"
                    needleBaseColor="#888"
                    textColor="#000"
                    formatTextValue={() => ""}
                  />

                  <div
                    className={`${styles.gaugeHoverArea} ${styles.left}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Purchase received amount", todayPurchaseAmount)}
                    onMouseLeave={handleMouseLeave}
                  />
                  <div
                    className={`${styles.gaugeHoverArea} ${styles.right}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Purchase Pending amount", todayPendingPurchaseAmount)}
                    onMouseLeave={handleMouseLeave}
                  />

                  <div className={styles.gaugeLabelsContainer}>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Purchase received amount</span>
                      <strong>₹ {todayPurchaseAmount}</strong>
                    </div>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Purchase Pending amount</span>
                      <strong>₹ {todayPendingPurchaseAmount}</strong>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div className={styles.grids}>
          {/* My Users - Static */}
          <div className={styles.card}>
            <div className={styles.header}>
              <h4 className={styles.heading}>Direct link, My User & Notifications</h4>
              <span onClick={() => navigate("/dashboard/Notifications")}>View all</span>
            </div>
            <div className={styles.overviewGridsuser}>


              <div className={`${styles.box} ${styles.colorloght}`}>
                <div className={styles.quickbtn}>

                  <button onClick={() => navigate("/dashboard/AddNewItems")} className={styles.addButtonstock}>+ Buy Items</button>
                  <button onClick={() => navigate("/dashboard/AddBilling")} className={styles.addButtonstock}>+ Sell Items</button>
                  <button onClick={() => navigate("/dashboard/CreateOrders")} className={styles.addButtonstock}>+ Order Items</button>
                </div>

              </div>
              <div className={`${styles.boxbanner} ${styles.lightblack}`}>
                <FiUserPlus className={styles.icon} />
                <h3>{dashboard.myActiveEmployeesCount || 0}</h3>
                <p>Total Active Employee</p>
              </div>

              <div className={styles.notificationsfarmerbanner}>
                {dashboard.notifications?.slice(0, 10).map((note) => {
                  const isUnread = note.isRead === false;

                  return (
                    <div
                      key={note._id}
                      className={`${styles.notification} ${isUnread ? styles.unreadNotification : ""}`}
                    >
                      <img
                        src={
                          note.createdBy?.profile
                            ? `${BaseURL}/${note.createdBy?.profile}`
                            : dummyProfile
                        }
                        alt={note.createdBy?.name || "User"}
                      />
                      <div>
                        <p>{note.message}</p>
                        <span
                          className={isUnread ? styles.redText : ""}
                        >
                          <FiClock
                            className={isUnread ? styles.redIcon : ""}
                          />{" "}
                          {new Date(note.createdAt).toLocaleDateString("en-GB")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>

          {/* Sales & Purchase Overview Flex Container */}
          {/* <div className={styles.card}>
            <div className={styles.overviewChartContainer}>

              <div className={styles.chartSection}>
                <div className={styles.header}>
                  <h4 className={styles.heading} onClick={() => navigate("/dashboard/billing", { state: { activeTab: "received" } })} style={{ cursor: "pointer" }}>Today’s Sales Overview</h4>
                  <span onClick={() => navigate("/dashboard/billing", { state: { activeTab: "received" } })}>
                    View all
                  </span>
                </div>
                <div className={styles.gaugeScaleWrapper}>
                  <GaugeChart
                    id="credit-balance-meter-sales"
                    nrOfLevels={20}
                    arcsLength={[salePayableRatio, saleReceivableRatio]}
                    colors={["#bfb8da", "#e8b7d4"]}
                    percent={saleReceivableRatio}
                    arcPadding={0.02}
                    needleColor="#222"
                    needleBaseColor="#888"
                    textColor="#000"
                    formatTextValue={() => ""}
                  />

                  <div
                    className={`${styles.gaugeHoverArea} ${styles.left}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Sales received amount", todaySaleAmount)}
                    onMouseLeave={handleMouseLeave}
                  />
                  <div
                    className={`${styles.gaugeHoverArea} ${styles.right}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Sales Pending amount", todayPendingSaleAmount)}
                    onMouseLeave={handleMouseLeave}
                  />

                  {tooltip.show && (
                    <div
                      style={{
                        position: "fixed",
                        top: tooltip.y,
                        left: tooltip.x,
                        transform: "translate(-50%, -100%)",
                        background: "#333",
                        color: "#fff",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        zIndex: 1000,
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        pointerEvents: "none"
                      }}
                    >
                      {tooltip.text}
                    </div>
                  )}

                  <div className={styles.gaugeLabelsContainer}>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Sales received amount</span>
                      <strong>₹ {todaySaleAmount}</strong>
                    </div>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Sales Pending amount</span>
                      <strong>₹ {todayPendingSaleAmount}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.chartSection}>
                <div className={styles.header}>
                  <h4 className={styles.heading} onClick={() => navigate("/dashboard/billing", { state: { activeTab: "payable" } })} style={{ cursor: "pointer" }}>Today’s Purchase Overview</h4>
                  <span onClick={() => navigate("/dashboard/billing", { state: { activeTab: "payable" } })}>
                    View all
                  </span>
                </div>
                <div className={styles.gaugeScaleWrapper}>
                  <GaugeChart
                    id="credit-balance-meter-purchase"
                    nrOfLevels={20}
                    arcsLength={[purchasePayableRatio, purchaseReceivableRatio]}
                    colors={["#bfb8da", "#e8b7d4"]}
                    percent={purchaseReceivableRatio}
                    arcPadding={0.02}
                    needleColor="#222"
                    needleBaseColor="#888"
                    textColor="#000"
                    formatTextValue={() => ""}
                  />

                  <div
                    className={`${styles.gaugeHoverArea} ${styles.left}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Purchase received amount", todayPurchaseAmount)}
                    onMouseLeave={handleMouseLeave}
                  />
                  <div
                    className={`${styles.gaugeHoverArea} ${styles.right}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "Total Purchase Pending amount", todayPendingPurchaseAmount)}
                    onMouseLeave={handleMouseLeave}
                  />

                  <div className={styles.gaugeLabelsContainer}>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Purchase received amount</span>
                      <strong>₹ {todayPurchaseAmount}</strong>
                    </div>
                    <div className={styles.gaugeLabelItem}>
                      <span>Total Purchase Pending amount</span>
                      <strong>₹ {todayPendingPurchaseAmount}</strong>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div> */}
           <div className={styles.bannerCard}>
            <div className={styles.header}>
              <h4 className={styles.heading}>Banner</h4>
              <span
                style={{ cursor: 'pointer', color: '#4CAF50', fontSize: '13px', fontWeight: 500 }}
                onClick={() => navigate('/dashboard/BannerSettings')}
              >
                View All
              </span>
            </div>
            <div className={styles.bannerSliderWrapper}>
              {data?.data?.bannerImages?.length > 0 ? (
                <Slider
                  dots={true}
                  infinite={true}
                  speed={800}
                  slidesToShow={1}
                  slidesToScroll={1}
                  autoplay={true}
                  autoplaySpeed={3500}
                  arrows={false}
                >
                  {data.data.bannerImages.map((img, idx) => (
                    <div key={idx} className={styles.bannerSlide}>
                      <img
                        src={typeof img === 'string' && img.startsWith('http') ? img : `${BaseURL}/${String(img).replace(/^\/+/, '')}`}
                        alt={`banner-${idx}`}
                        className={styles.bannerimg}
                        onError={(e) => (e.target.src = dummyProfile)}
                      />
                    </div>
                  ))}
                </Slider>
              ) : (
                <div className={styles.boxs}>
                  <img className={styles.bannerimg} src={bannerImage} alt="Store Banner" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderDashboardContent()}

      {showExpiryPopup && (
        <PopupWrapper onClose={handleExpiryPopupClose}>
          <div className={popupStyles.popupForm} >
            <div className={popupStyles.header}>
              <h2 className={popupStyles.tittle}>
                Subscription Alert
              </h2>
              <span className={popupStyles.closeIcons} onClick={handleExpiryPopupClose}>
                X
              </span>
            </div>

            <div style={{ padding: '20px 30px', textAlign: 'center' }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                Your current plan <strong>{user?.currentPlan?.planName}</strong> {expiryDays >= 0 ? `will expire in` : `has expired`} <strong>{Math.abs(expiryDays)} {Math.abs(expiryDays) === 1 ? 'day' : 'days'}</strong>{expiryDays < 0 ? ' ago' : ''}.
              </p>
              <p style={{ fontSize: '14px', color: '#6d6d6d' }}>
                {expiryDays >= 0 ? 'Please renew your subscription to enjoy uninterrupted services.' : 'Please renew your subscription immediately to restore services.'}
              </p>
            </div>

            <div className={popupStyles.footerButtons} style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className={popupStyles.save}
                onClick={() => {
                  handleExpiryPopupClose();
                  navigate('/dashboard/SubscriptionUser');
                }}
              >
                Renew Now
              </button>
            </div>
          </div>
        </PopupWrapper>
      )}

      {showInvitePopup && (
        <PopupWrapper onClose={handleInvitePopupClose}>
          <div className={popupStyles.popupForm} >
            <div className={popupStyles.header}>
              <h2 className={popupStyles.tittle}>
                Invite Alert
              </h2>
              <span className={popupStyles.closeIcons} onClick={handleInvitePopupClose}>
                X
              </span>
            </div>

            <div style={{ padding: '20px 30px', textAlign: 'center' }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                You have <strong>{inviteCount}</strong> pending {inviteCount === 1 ? 'invite' : 'invites'}.
              </p>
              <p style={{ fontSize: '14px', color: '#6d6d6d' }}>
                Please review and accept your invites to proceed.
              </p>
            </div>

            <div className={popupStyles.footerButtons} style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className={popupStyles.save}
                onClick={() => {
                  handleInvitePopupClose();
                  navigate('/dashboard/Invite?tab=Invite From');
                }}
              >
                View Invites
              </button>
            </div>
          </div>
        </PopupWrapper>
      )}
    </>
  );
};

export default Dashboard;



